import { FC, useState } from "react";
import { GroupButton } from "../../components/GroupButton";
import useMultiLanguage from "../../../hook/useMultiLanguage";
import { createAccountDefault, generateOfficeWork, generateTimesheet, reloadAttribution, reloadCategoryValue, reloadDepartment, reloadMappingSymbol, reloadOrganization, reloadSymbol, reloadUser, updateEmployeeInfo } from "../services/toolSupportServices";
import { toast } from "react-toastify";
import { RESPONSE_STATUS_CODE, VARIABLE_STRING } from "../../constants/moduleConsts";
import { Col, Form, Modal, Row } from "react-bootstrap";
import TextValidator from "../../components/input/text-validator";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IToolSupportParams } from "../models/managerModels";
import { INIT_TOOL_SUPPORT_PARAMS, LIST_INFO_UPDATE_EMPLOYEE } from "../constants/managerConsts";
import Autocomplete from "../../components/input/autocomplete";
import { OptionReactSelect } from "../../models/models";

const ToolSupport: FC = () => {
  const { lang } = useMultiLanguage();
  const [showDialog, setShowDialog] = useState("");

  const validationSchema = Yup.object().shape({
    option: Yup.object()
      .concat(showDialog === VARIABLE_STRING.TOOL_UPDATE_EMPLOYEE 
        ? Yup.object().required(lang("VALIDATION.REQUIRE")) 
        : Yup.object()
      )
      .nullable(),
    date: Yup.date()
      .concat((showDialog === VARIABLE_STRING.TOOL_GENERATE_OFFICE_WORK 
        || showDialog === VARIABLE_STRING.TOOL_GENERATE_TIMESHEET)
          ? Yup.date().required(lang("VALIDATION.REQUIRE")) 
          : Yup.date()
      )
      .nullable()
  });

  const handleSubmit = async (values: IToolSupportParams) => {
    try {
      let res;
      switch (showDialog) {
        case VARIABLE_STRING.TOOL_UPDATE_EMPLOYEE:
          res = await updateEmployeeInfo(values?.option?.code || null);
          break;
          case VARIABLE_STRING.TOOL_GENERATE_OFFICE_WORK:
          res = await generateOfficeWork({ date: values?.date });
          break;
          case VARIABLE_STRING.TOOL_GENERATE_TIMESHEET:
          res = await generateTimesheet({ date: values?.date });
          break;
      }
      if (res?.data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        let message = "TOAST.EDIT.SUCCESS";
        toast.success(lang(message));
        handleCloseDialog();
      } else toast.warning(res?.data?.message);
    } catch (err) {
      toast.error(lang("GENERAL.ERROR"));
    }
  };
  
  const formik = useFormik({
    initialValues: INIT_TOOL_SUPPORT_PARAMS,
    onSubmit: handleSubmit,
    validationSchema
  });

  const handleReloadCacheCategoryValue = async () => {
    try {
      const { data } = await reloadCategoryValue();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }
  
  const handleReloadUser = async () => {
    try {
      const { data } = await reloadUser();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }
  
  const handleReloadDepartment = async () => {
    try {
      const { data } = await reloadDepartment();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }
  
  const handleReloadOrganization = async () => {
    try {
      const { data } = await reloadOrganization();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }

  const handleCreateAccountDefault = async () => {
    try {
      const res = await createAccountDefault();
      if (res) {
        toast.success(lang("TOAST.CREATE_ACCOUNT.SUCCESS"));
      }
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }

  const handleReloadAttribution = async () => {
    try {
      const { data } = await reloadAttribution();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }

  const handleReloadMappingSymbol = async () => {
    try {
      const { data } = await reloadMappingSymbol();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }

  const handleReloadSymbol = async () => {
    try {
      const { data } = await reloadSymbol();
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.RELOAD.SUCCESS"));
      } else toast.warning(data?.message);
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    }
  }

  const handleCloseDialog = () => {
    setShowDialog("");
    formik.resetForm();
  }

  const handleChangeSelect = (name: string, value: OptionReactSelect) => {
    formik.setFieldValue(name, value);
  }

  return (
    <div className="my-6 px-4">
      <h3>{lang("GENERAL.APP.NAME")}</h3>
      <div className="flex-wrap gap-3 mt-2 mb-6">
        <GroupButton outline handleEvent={handleReloadCacheCategoryValue}>
          {lang("TOOL_SUPPORT.RELOAD_CATEGORY_VALUE")}
        </GroupButton>
        <GroupButton outline handleEvent={handleReloadUser}>
          {lang("TOOL_SUPPORT.RELOAD_USER")}
        </GroupButton>
        <GroupButton outline handleEvent={handleReloadDepartment}>
          {lang("TOOL_SUPPORT.RELOAD_DEPARTMENT")}
        </GroupButton>
        <GroupButton outline handleEvent={handleReloadOrganization}>
          {lang("TOOL_SUPPORT.RELOAD_ORGANIZATION")}
        </GroupButton>
        <GroupButton outline handleEvent={() => setShowDialog(VARIABLE_STRING.TOOL_UPDATE_EMPLOYEE)}>
          {lang("TOOL_SUPPORT.UPDATE_EMPLOYEE_INFO")}
        </GroupButton>
        <GroupButton outline handleEvent={handleCreateAccountDefault}>
          {lang("TOOL_SUPPORT.CREATE_ACCOUNT_DEFAULT")}
        </GroupButton>
      </div>
      <h3>{lang("GENERAL.TIME_KEEPING")}</h3>
      <div className="flex-wrap gap-3 mt-2 mb-6">
        <GroupButton outline handleEvent={handleReloadAttribution}>
          {lang("TOOL_SUPPORT.RELOAD_ATTRIBUTION")}
        </GroupButton>
        <GroupButton outline handleEvent={() => setShowDialog(VARIABLE_STRING.TOOL_GENERATE_OFFICE_WORK)}>
          {lang("TOOL_SUPPORT.GENERATE_OFFICE_WORK")}
        </GroupButton>
        <GroupButton outline handleEvent={() => setShowDialog(VARIABLE_STRING.TOOL_GENERATE_TIMESHEET)}>
          {lang("TOOL_SUPPORT.GENERATE_TIMESHEET")}
        </GroupButton>
        <GroupButton outline handleEvent={handleReloadMappingSymbol}>
          {lang("TOOL_SUPPORT.RELOAD_MAPPING_SYMBOL")}
        </GroupButton>
        <GroupButton outline handleEvent={handleReloadSymbol}>
          {lang("TOOL_SUPPORT.RELOAD_SYMBOL")}
        </GroupButton>
      </div>
      {showDialog && (
        <Modal
          show={true}
          onHide={handleCloseDialog}
          backdrop="static"
          size="sm"
          centered
        >
          <Form onSubmit={formik.handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>{lang("TOOL_SUPPORT.PARAMS")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="px-8 gap-2">
                {showDialog === VARIABLE_STRING.TOOL_UPDATE_EMPLOYEE &&
                  <Col xs={12}>
                    <Autocomplete
                      isRequired
                      options={LIST_INFO_UPDATE_EMPLOYEE}
                      lable={lang("TOOL_SUPPORT.UPDATE_EMPLOYEE_INFO.TITLE")}
                      name="option"
                      value={formik.values?.option || null}
                      onChange={(selectedOption) => handleChangeSelect("option", selectedOption)}
                      errors={formik?.errors?.option}
                      touched={formik?.touched?.option}
                    />
                  </Col>
                }
                {(showDialog === VARIABLE_STRING.TOOL_GENERATE_OFFICE_WORK 
                  || showDialog === VARIABLE_STRING.TOOL_GENERATE_TIMESHEET) &&
                    <Col xs={12}>
                      <TextValidator
                        isRequired
                        name="date"
                        lable={lang("GENERAL.TIME")}
                        type="date"
                        value={formik.values?.date || ""}
                        onChange={formik.handleChange}
                        errors={formik.errors?.date}
                        touched={formik.touched?.date}
                        />
                    </Col>
                }
              </Row>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center">
              <GroupButton
                type="btn-cancel"
                handleCloseUpdateDialog={handleCloseDialog}
              />
              <GroupButton typeButton="submit">{lang("AUTH.GENERAL.SUBMIT_BUTTON")}</GroupButton>
            </Modal.Footer>
          </Form>
        </Modal>
      )}
    </div>
  );
}

export default ToolSupport;